import {
  createSlice,
  isRejected,
  isFulfilled,
  isPending,
} from '@reduxjs/toolkit';
import { RootState } from '../..';
import { ILessonHistory } from '../LessonHistorySlice';
import {
  ISection,
  TLessonTypes,
  TMeetingSectionPaymentType,
} from '../SectionSlice';
import { IUser } from '../../UserSlice';
import {
  adminCreatePenaltyWaiver,
  adminGetPenaltyWaiverById,
  adminGetPenaltyWaivers,
  adminRespondToPenaltyAppeal,
  adminUpdatePenaltyWaiverTutorPaymentStatus,
} from './adminApi';
import {
  createPenaltyAppealRequest,
  getPenaltyWaiverById,
  getPenaltyWaivers,
} from './api';
import { ICourse } from '../CoursesSlice';
import { IManualAdjustment } from '../ManualAdjustmentsSlice';

export const PENALTY_WAIVER_TYPES = {
  ADDITION: 'ADDITION',
  DEDUCTION: 'DEDUCTION',
} as const;

export const PENALTY_WAIVER_PAYMENT_STATUSES = {
  PROCESSING: 'PROCESSING',
  PAID: 'PAID',
} as const;

export const PENALTY_WAIVER_APPEAL_STATUSES = {
  PENDING: 'PENDING',
  ACCEPTED: 'ACCEPTED',
  REJECTED: 'REJECTED',
} as const;

export type TPenaltyWaiverTypes = typeof PENALTY_WAIVER_TYPES[keyof typeof PENALTY_WAIVER_TYPES];
export type TPenaltyWaiverPaymentStatus = typeof PENALTY_WAIVER_PAYMENT_STATUSES[keyof typeof PENALTY_WAIVER_PAYMENT_STATUSES];
export type TPenaltyWaiverAppealStatus = typeof PENALTY_WAIVER_APPEAL_STATUSES[keyof typeof PENALTY_WAIVER_APPEAL_STATUSES];

export interface IPenaltyWaiverFile {
  name: string;
  url: string;
}

export type TPenaltyWaiverTutorAmountData = {
  manualAdjustments: Array<IManualAdjustment>;
  tutorPenaltyAmountData: { [key: string]: number };
  // Paid
  paidTotalData: { [key: string]: number };
  pendingTotalData: { [key: string]: number };
  paidTutorPenaltyData: { [key: string]: number };
  paidStudentPenaltyData: { [key: string]: number };
  paidPenaltyWaiverData: { [key: string]: number };
  manualAdjustmentData: { [key: string]: number };
  paidAmountData: { [key: string]: number };
  lessonHistoryPaidTutorPenalties: { [key: string]: number };
  // PENDING
  pendingTutorPenaltyData: { [key: string]: number };
  pendingStudentPenaltyData: { [key: string]: number };
  pendingPenaltyWaiverData: { [key: string]: number };
  pendingAmountData: { [key: string]: number };
  lessonHistoryPendingTutorPenalties: { [key: string]: number };
};

export interface IPenaltyWaiver {
  _id: string;
  section: string | ISection;
  course: string | ICourse;
  createdBy: string | IUser;
  updatedBy: string | IUser;
  type: TPenaltyWaiverTypes;
  amount: number;
  currency: string;
  paymentStatus: TPenaltyWaiverPaymentStatus;
  tutor: string | IUser;
  students: Array<string | IUser>;
  startDate: string;
  endDate: string;
  lessonType: TLessonTypes;
  paymentType: TMeetingSectionPaymentType;
  createdAt: string;
  updatedAt: string;
  lessonHistory?: string | ILessonHistory;
  appealReason?: string;
  appealStatus?: TPenaltyWaiverAppealStatus;
  appealAdminNote?: string;
  appealTutorNote?: string;
  appealRespondedBy?: string | IUser;
  files?: IPenaltyWaiverFile[];
  referenceId: string;
}

export interface IInitialPenaltyWaiverState {
  status: 'idle' | 'loading' | 'rejected';
  penaltyWaiverListData?: {
    imit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IPenaltyWaiver>;
    tutorAmountData?: TPenaltyWaiverTutorAmountData;
  };
  penaltyWaiverData?: IPenaltyWaiver;
  // Admin
  adminPenaltyWaiverListData?: {
    imit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IPenaltyWaiver>;
    tutorAmountData?: TPenaltyWaiverTutorAmountData;
  };
  adminPenaltyWaiverData?: IPenaltyWaiver;
}

const initialState: IInitialPenaltyWaiverState = {
  status: 'idle',
  penaltyWaiverListData: undefined,
  penaltyWaiverData: undefined,
  // Admin
  adminPenaltyWaiverListData: undefined,
  adminPenaltyWaiverData: undefined,
};

export const PenaltyWaiverSlice = createSlice({
  initialState,
  name: 'penaltyWaiver',
  reducers: {
    resetPenaltyWaiverData: (state) => {
      state.penaltyWaiverData = undefined;
    },
    // Admin
    resetAdminPenaltyWaiverListData: (state) => {
      state.adminPenaltyWaiverListData = undefined;
    },
    resetAdminPenaltyWaiverData: (state) => {
      state.adminPenaltyWaiverData = undefined;
    },
    resetPenaltyWaiverListData: (state) => {
      state.penaltyWaiverListData = undefined;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getPenaltyWaivers.fulfilled, (state, action) => {
        const result = action.payload;

        const newPenaltyWaiverListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
          tutorAmountData: result.tutorAmountData,
        };

        state.penaltyWaiverListData = newPenaltyWaiverListData;
      })
      .addCase(getPenaltyWaiverById.fulfilled, (state, action) => {
        const result = action.payload;

        state.penaltyWaiverData = result;
      });

    // Admin
    builder
      .addCase(adminGetPenaltyWaivers.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminPenaltyWaiverListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
          tutorAmountData: result.tutorAmountData,
        };

        state.adminPenaltyWaiverListData = newAdminPenaltyWaiverListData;
      })
      .addCase(adminCreatePenaltyWaiver.fulfilled, (state, action) => {
        const result = action.payload;

        if (state.adminPenaltyWaiverListData) {
          const newAdminPenaltyWaiverListData = {
            ...state.adminPenaltyWaiverListData,
          };

          newAdminPenaltyWaiverListData.list.unshift(result);

          newAdminPenaltyWaiverListData.totalDocs =
            (newAdminPenaltyWaiverListData.totalDocs || 0) + 1;

          state.adminPenaltyWaiverListData = newAdminPenaltyWaiverListData;
        }
      })
      .addCase(adminGetPenaltyWaiverById.fulfilled, (state, action) => {
        const result = action.payload;

        state.adminPenaltyWaiverData = result;
      })
      .addCase(
        adminUpdatePenaltyWaiverTutorPaymentStatus.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (
            state.adminPenaltyWaiverListData &&
            result.paymentStatus &&
            result.penaltyWaiverIds &&
            result.penaltyWaiverIds.length
          ) {
            const newAdminPenaltyWaiverListData = {
              ...state.adminPenaltyWaiverListData,
            };

            const newList = [...state.adminPenaltyWaiverListData.list].map(
              (item) => {
                if ((result.penaltyWaiverIds || []).includes(item._id)) {
                  return {
                    ...item,
                    paymentStatus: result.paymentStatus,
                  };
                }

                return item;
              }
            );

            newAdminPenaltyWaiverListData.list = newList;

            state.adminPenaltyWaiverListData = newAdminPenaltyWaiverListData;
          }
        }
      )
      .addCase(adminRespondToPenaltyAppeal.fulfilled, (state, action) => {
        const result = action.payload;

        state.adminPenaltyWaiverData = result;

        if (state.adminPenaltyWaiverListData) {
          const newAdminPenaltyWaiverListData = {
            ...state.adminPenaltyWaiverListData,
          };

          const newList = [...(state.adminPenaltyWaiverListData.list || [])];

          const index = newList.findIndex((item) => item._id === result._id);

          if (index > -1) {
            newList[index] = {
              ...newList[index],
              appealStatus: result.appealStatus,
              appealAdminNote: result.appealAdminNote,
              appealTutorNote: result.appealTutorNote,
              amount: result.amount,
              appealRespondedBy: result.appealRespondedBy,
            };
          }

          newAdminPenaltyWaiverListData.list = newList;

          state.adminPenaltyWaiverListData = newAdminPenaltyWaiverListData;
        }
      });

    // Common Matchers
    builder.addMatcher(
      isFulfilled(
        createPenaltyAppealRequest,
        getPenaltyWaivers,
        getPenaltyWaiverById,
        // ADMIN
        adminCreatePenaltyWaiver,
        adminGetPenaltyWaivers,
        adminUpdatePenaltyWaiverTutorPaymentStatus,
        adminGetPenaltyWaiverById,
        adminRespondToPenaltyAppeal
      ),
      (state) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isRejected(
        createPenaltyAppealRequest,
        getPenaltyWaivers,
        getPenaltyWaiverById,
        // ADMIN
        adminCreatePenaltyWaiver,
        adminGetPenaltyWaivers,
        adminUpdatePenaltyWaiverTutorPaymentStatus,
        adminGetPenaltyWaiverById,
        adminRespondToPenaltyAppeal
      ),
      (state) => {
        state.status = 'rejected';
      }
    );

    builder.addMatcher(
      isPending(
        createPenaltyAppealRequest,
        getPenaltyWaivers,
        getPenaltyWaiverById,
        // ADMIN
        adminCreatePenaltyWaiver,
        adminGetPenaltyWaivers,
        adminUpdatePenaltyWaiverTutorPaymentStatus,
        adminGetPenaltyWaiverById,
        adminRespondToPenaltyAppeal
      ),
      (state) => {
        state.status = 'loading';
      }
    );
  },
});

export const getPenaltyWaiverState = (
  state: RootState
): RootState['penaltyWaiver'] => state.penaltyWaiver;

export const {
  resetPenaltyWaiverListData,
  resetPenaltyWaiverData,
  // ADMIN
  resetAdminPenaltyWaiverListData,
  resetAdminPenaltyWaiverData,
} = PenaltyWaiverSlice.actions;

export * from './api';
export * from './adminApi';
export default PenaltyWaiverSlice.reducer;
